import type { RouteObject } from "react-router-dom";
import NotFound from "../pages/NotFound";
import Home from "../pages/home/page";
import InvestmentsPage from "../pages/home/subpages/InvestmentsPage";
import WhyInvestPage from "../pages/home/subpages/WhyInvestPage";
import ReturnsPage from "../pages/home/subpages/ReturnsPage";
import ResortPage from "../pages/home/subpages/ResortPage";
import ModelsPage from "../pages/home/subpages/ModelsPage";
import VillaDetail from "../pages/home/VillaDetail";
import AdminLayout from "../pages/admin/layout";
import AdminLogin from "../pages/admin/login/page";
import AdminDashboard from "../pages/admin/dashboard/page";
import AdminProperties from "../pages/admin/properties/page";
import AdminLeads from "../pages/admin/leads/page";
import AdminDeals from "../pages/admin/deals/page";
import AdminReminders from "../pages/admin/reminders/page";
import AdminTransactions from "../pages/admin/transactions/page";
import AdminContent from "../pages/admin/content/page";
import AdminPages from "../pages/admin/pages/page";
import AdminMedia from "../pages/admin/media/page";
import AdminReports from "../pages/admin/reports/page";
import AdminUsers from "../pages/admin/users/page";
import AdminSettings from "../pages/admin/settings/page";
import AdminSecurity from "../pages/admin/security/page";
import PublicLayout from "../pages/public/components/PublicLayout";
import CmsPage from "../components/cms/CmsPage";
import PublicListings from "../pages/public/listings/page";
import PublicServices from "../pages/public/services/page";
import PublicHowItWorks from "../pages/public/how-it-works/page";
import PublicPricing from "../pages/public/pricing/page";
import PublicFaq from "../pages/public/faq/page";
import PublicContact from "../pages/public/contact/page";
import PublicAbout from "../pages/public/about/page";
import PublicCaseStudies from "../pages/public/case-studies/page";
import PublicAiTechnology from "../pages/public/ai-technology/page";
import PublicInvestmentGuide from "../pages/public/investment-guide/page";
import PublicProjects from "../pages/public/projects/page";
import ProjectDetail from "../pages/public/projects/ProjectDetail";
import PublicDestinations from "../pages/public/destinations/page";
import PublicPress from "../pages/public/press/page";

const routes: RouteObject[] = [
  {
    path: "/",
    element: <Home />,
  },
  {
    path: "/investments",
    element: <InvestmentsPage />,
  },
  {
    path: "/why-invest",
    element: <WhyInvestPage />,
  },
  {
    path: "/returns",
    element: <ReturnsPage />,
  },
  {
    path: "/resort",
    element: <ResortPage />,
  },
  {
    path: "/models",
    element: <ModelsPage />,
  },
  {
    path: "/villa/:slug",
    element: <VillaDetail />,
  },
  {
    element: <PublicLayout />,
    children: [
      {
        path: "listings",
        element: (
          <CmsPage slug="listings" path="/listings" fallbackSeo={{ title: "A-Frame Villa Models | NOMADLIVINGS", description: "Explore our modular A-frame villa models engineered for hospitality, comfort and strong rental returns across India." }}>
            <PublicListings />
          </CmsPage>
        ),
      },
      {
        path: "services",
        element: (
          <CmsPage slug="services" path="/services" fallbackSeo={{ title: "Services | NOMADLIVINGS", description: "Villa design, build, resort operations and investment services from the Nomad Livings hospitality arm." }}>
            <PublicServices />
          </CmsPage>
        ),
      },
      {
        path: "how-it-works",
        element: (
          <CmsPage slug="how-it-works" path="/how-it-works" fallbackSeo={{ title: "How It Works | NOMADLIVINGS", description: "A clear step-by-step guide to investing in a Nomad Livings modular A-frame villa." }}>
            <PublicHowItWorks />
          </CmsPage>
        ),
      },
      {
        path: "pricing",
        element: (
          <CmsPage slug="pricing" path="/pricing" fallbackSeo={{ title: "Pricing & Villa Types | NOMADLIVINGS", description: "Villa pricing, configurations and investment entry points for Nomad Livings A-frame properties." }}>
            <PublicPricing />
          </CmsPage>
        ),
      },
      {
        path: "faq",
        element: (
          <CmsPage slug="faq" path="/faq" fallbackSeo={{ title: "FAQ | NOMADLIVINGS", description: "Answers to common questions about investing in a Nomad Livings A-frame villa." }}>
            <PublicFaq />
          </CmsPage>
        ),
      },
      {
        path: "contact",
        element: (
          <CmsPage slug="contact" path="/contact" fallbackSeo={{ title: "Contact | NOMADLIVINGS", description: "Reach out to Nomad Livings for a free villa investment consultation. Our advisors respond within 24 hours." }}>
            <PublicContact />
          </CmsPage>
        ),
      },
      {
        path: "about",
        element: (
          <CmsPage slug="about" path="/about" fallbackSeo={{ title: "About | NOMADLIVINGS", description: "Nomad Livings is the hospitality investment arm of the Govianu Group with 15+ years of real estate expertise." }}>
            <PublicAbout />
          </CmsPage>
        ),
      },
      {
        path: "case-studies",
        element: (
          <CmsPage slug="case-studies" path="/case-studies" fallbackSeo={{ title: "Case Studies | NOMADLIVINGS", description: "Real investor stories and performance of Nomad Livings modular A-frame villa projects." }}>
            <PublicCaseStudies />
          </CmsPage>
        ),
      },
      {
        path: "ai-technology",
        element: (
          <CmsPage slug="ai-technology" path="/ai-technology" fallbackSeo={{ title: "AI Technology | NOMADLIVINGS", description: "How Nomad Livings uses AI to optimize villa design, pricing and hospitality returns." }}>
            <PublicAiTechnology />
          </CmsPage>
        ),
      },
      {
        path: "investment-guide",
        element: (
          <CmsPage slug="investment-guide" path="/investment-guide" fallbackSeo={{ title: "Investment Guide | NOMADLIVINGS", description: "Download the 2026 Nomad Livings investment guide with full villa specs and projected returns." }}>
            <PublicInvestmentGuide />
          </CmsPage>
        ),
      },
      {
        path: "projects",
        element: (
          <CmsPage slug="projects" path="/projects" fallbackSeo={{ title: "Projects | NOMADLIVINGS", description: "Explore Nomad Livings modular A-frame villa projects across India, from Coorg to Rishikesh." }}>
            <PublicProjects />
          </CmsPage>
        ),
      },
      {
        path: "projects/:slug",
        element: <ProjectDetail />,
      },
      {
        path: "destinations",
        element: (
          <CmsPage slug="destinations" path="/destinations" fallbackSeo={{ title: "Destinations | NOMADLIVINGS", description: "Discover the hospitality destinations where Nomad Livings A-frame villas are built." }}>
            <PublicDestinations />
          </CmsPage>
        ),
      },
      {
        path: "press",
        element: (
          <CmsPage slug="press" path="/press" fallbackSeo={{ title: "Press & News | NOMADLIVINGS", description: "Latest news, coverage and updates from Nomad Livings." }}>
            <PublicPress />
          </CmsPage>
        ),
      },
    ],
  },
  {
    path: "/admin/login",
    element: <AdminLogin />,
  },
  {
    path: "/admin",
    element: <AdminLayout />,
    children: [
      { index: true, element: <AdminDashboard /> },
      { path: "properties", element: <AdminProperties /> },
      { path: "leads", element: <AdminLeads /> },
      { path: "deals", element: <AdminDeals /> },
      { path: "reminders", element: <AdminReminders /> },
      { path: "transactions", element: <AdminTransactions /> },
      { path: "content", element: <AdminContent /> },
      { path: "pages", element: <AdminPages /> },
      { path: "media", element: <AdminMedia /> },
      { path: "reports", element: <AdminReports /> },
      { path: "users", element: <AdminUsers /> },
      { path: "settings", element: <AdminSettings /> },
      { path: "security", element: <AdminSecurity /> },
    ],
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;